import React from "react";

import RouteFallback from "@/sot/primitives/RouteFallback.jsx";

const RETRY_DELAYS_MS = [0, 100, 300];
const FAILURE_DELAY_MS = 600;

/**
 * Opens the application-shell CookieConsent UI for a controlled first-use
 * cookies stage. This deliberately does not create a second consent flow:
 * CookieConsent remains responsible for recording a real decision and
 * advancing the server-governed first-use stage.
 */
export default function FirstUseCookieGate() {
  const openedRef = React.useRef(false);
  const [unavailable, setUnavailable] = React.useState(false);

  React.useEffect(() => {
    const openConsent = () => {
      if (openedRef.current || !window.afrimedCookies?.open) return;
      openedRef.current = true;
      window.afrimedCookies.open();
    };

    // CookieConsent mounts at the application shell. A short, bounded retry
    // covers resumed auth callbacks without permitting an infinite loader.
    const timers = RETRY_DELAYS_MS.map((delay) => window.setTimeout(openConsent, delay));
    const failureTimer = window.setTimeout(() => {
      if (!openedRef.current) setUnavailable(true);
    }, FAILURE_DELAY_MS);

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(failureTimer);
    };
  }, []);

  if (unavailable) {
    return (
      <main className="min-h-dvh grid place-items-center p-6 text-center">
        <div className="max-w-md space-y-3">
          <h1 className="text-title-large text-on-surface">Cookie preferences are unavailable</h1>
          <p className="text-body-medium text-on-surface-var">
            Please refresh this page to continue setting up your AfriMed™ account.
          </p>
        </div>
      </main>
    );
  }

  return <RouteFallback label="Preparing your AfriMed™ setup..." />;
}
